import { Add, Upload } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Checkbox, Grid2, Paper, Typography } from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMPopUp } from "@pimo/pimo-components";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  Project,
} from "product-test-types";
import { checkField, VIEW_NAMES } from "product-test-utils";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  getItemName,
  getPluralOfItemName,
} from "../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";
import { BasicItemRow } from "./basic-item-row";

export type ItemManagerProps = {
  basicItems: BasicItem[];
  projects?: Project[];
  configuration?: Configuration;
  language: SupportedLanguage;
};

export const ItemManagerPage: PimoReactComponent<
  ItemManagerProps,
  {
    basicItems?: BasicItem[];
    configuration?: Configuration;
    projects?: Project[];
  }
> = ({ language }) => {
  const { setValue, formState } = usePimoForm<{
    basicItems: BasicItem[];
    configuration?: Configuration;
    projects?: Project[];
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });

  const [openDelete, setOpenDelete] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<
    string[] | undefined
  >(undefined);

  const [selectedItemIndices, setSelectedItemIndices] = useState<string[]>([]);

  const projectName = {
    single: getProjectName(formState?.configuration?.projectName, language),
    plural: getPluralOfProjectName(
      formState?.configuration?.projectName,
      language
    ),
  };
  const itemName = {
    single: getItemName(formState?.configuration?.itemName, language),
    plural: getPluralOfItemName(formState?.configuration?.itemName, language),
  };

  const getFieldDefinition = useCallback(
    (fieldName: string) => {
      return formState.configuration?.fieldsDefinition
        ?.filter((f) => f.purpose === "itemBasic")
        ?.find((f) => f.fieldName === fieldName && f.enabled === true);
    },
    [formState.configuration]
  );

  const isMaturityUseCase = formState.configuration?.useCase === "maturity";
  const showMaturity =
    isMaturityUseCase && Boolean(getFieldDefinition("maturity"));
  const showDimension =
    isMaturityUseCase && Boolean(getFieldDefinition("dimension"));
  const itemNameColumnSize =
    9.5 - (showMaturity ? 2 : 0) - (showDimension ? 2 : 0);
  const showUploadExcel = formState.configuration?.useCase !== "pmo";

  const handleBasicItemFieldChange = useCallback(
    (id: string, fieldKey: string, value: unknown) => {
      const index = formState.basicItems.findIndex((item) => item.id === id);
      if (index === -1) {
        return;
      }

      const newErrors = {
        ...(formState.validationErrors ?? {}),
      } as Record<string, ExtendedFieldError>;

      const setFlagIfNeeded = () => {
        const item = formState.basicItems?.[index];
        if (item && item.flag !== "create" && item.flag !== "delete") {
          setValue(`basicItems.${index}.flag`, "update");
        }
      };

      const def = getFieldDefinition(fieldKey);

      switch (fieldKey) {
        case "name":
        case "description":
        case "customId":
        case "owner": {
          setValue(`basicItems.${index}.${fieldKey}`, value);

          if (!value && def?.isMandatory) {
            newErrors[`basicItems.${index}.${fieldKey}`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.${fieldKey}`];
          }
          checkField({
            iterableFieldName: `basicItems.{idx}.${fieldKey}`,
            idx: index,
            errors: newErrors,
            setErrors: (errors) => setValue("validationErrors", errors),
            value,
            tabContainingErrors: VIEW_NAMES[7],
            language,
          });

          setFlagIfNeeded();
          break;
        }

        case "projectIds": {
          // value is project id string
          const projectIds = value as string;
          if (projectIds === "allProjects") {
            setValue(
              `basicItems.${index}.projectIds`,
              formState.projects?.map((p) => p.id) ?? []
            );
            setValue(`basicItems.${index}.itemAssignedToAllProjects`, true);
            delete newErrors[`basicItems.${index}.projectIds`];
            setValue("validationErrors", newErrors);
            setFlagIfNeeded();
            break;
          } else {
            setValue(`basicItems.${index}.itemAssignedToAllProjects`, false);
            setValue(`basicItems.${index}.projectIds`, [projectIds]);

            if (!projectIds && def?.isMandatory) {
              newErrors[`basicItems.${index}.projectIds`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
              } as ExtendedFieldError;
            } else {
              delete newErrors[`basicItems.${index}.projectIds`];
            }

            setValue("validationErrors", newErrors);
            setFlagIfNeeded();
            break;
          }
        }
        case "budget": {
          const raw = value as string;
          setValue(`basicItems.${index}.budget`, parseFloat(raw));
          if (!raw && def?.isMandatory) {
            newErrors[`basicItems.${index}.budget`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else if (raw && isNaN(Number(raw))) {
            newErrors[`basicItems.${index}.budget`] = {
              type: "pattern",
              message: "This field must be a number",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.budget`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "startDate": {
          const dateVal = value as
            Dayjs | string | number | Date | null | undefined;
          setValue(`basicItems.${index}.startDate`, dateVal);

          let entry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            entry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            entry = {
              type: "invalid",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          }

          if (entry) {
            newErrors[`basicItems.${index}.startDate`] = entry;
          } else {
            delete newErrors[`basicItems.${index}.startDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "endDate": {
          const dateVal = value as
            Dayjs | string | number | Date | null | undefined;
          setValue(`basicItems.${index}.endDate`, dateVal);

          let newEntry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            newEntry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            newEntry = {
              type: "invalid",
              message: "This is not a valid date",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (
            dateVal &&
            formState.basicItems?.[index]?.startDate &&
            dayjs(dateVal).isBefore(
              dayjs(formState.basicItems?.[index]?.startDate)
            )
          ) {
            newEntry = {
              type: "invalid",
              message: "End date should be after start date",
              tabContainingErrors: VIEW_NAMES[7],
            };
          }

          if (newEntry) {
            newErrors[`basicItems.${index}.endDate`] = newEntry;
          } else {
            delete newErrors[`basicItems.${index}.endDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "dropdownFieldOne": {
          const val = value as string;
          setValue(`basicItems.${index}.dropdownFieldOne`, val);

          if (!val && def?.isMandatory) {
            newErrors[`basicItems.${index}.dropdownFieldOne`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.dropdownFieldOne`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "maturity": {
          const val = value as string;
          setValue(`basicItems.${index}.maturity`, val);

          if (!val && def?.isMandatory) {
            newErrors[`basicItems.${index}.maturity`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.maturity`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        default: {
          setValue(`basicItems.${index}.${fieldKey}`, value);
          setFlagIfNeeded();
          break;
        }
      }
      if (formState.enableSaveButton !== true) {
        setValue("enableSaveButton", true);
      }
    },
    [
      formState.basicItems,
      formState.validationErrors,
      formState.enableSaveButton,
      formState.projects,
      getFieldDefinition,
      language,
      setValue,
    ]
  );

  const handleAppend = () => {
    const newItem: BasicItem = {
      id: uuidv4(),
      name: "",
      description: "",
      owner: "",
      budget: 0,
      startDate: undefined,
      endDate: undefined,
      projectIds: formState.projects?.map((p) => p.id) ?? [],
      flag: "create",
      itemAssignedToAllProjects: true,
    };
    setValue("enableSaveButton", true);
    setValue("basicItems", [...(formState?.basicItems ?? []), newItem]);

    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (indices: string[]) => {
    setCurrentSelectedItem(indices);
    setOpenDelete(true);
  };
  const handleConfirmDelete = () => {
    if (currentSelectedItem === undefined) {
      return;
    }

    setValue(
      "basicItems",
      formState.basicItems.map((item) =>
        item.id && currentSelectedItem?.includes(item.id)
          ? { ...item, flag: "delete" }
          : item
      )
    );
    setValue("enableSaveButton", true);
    setOpenDelete(false);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "manageItems.title", {
          itemName: itemName.plural,
        })}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: "#000",
            fontSize: "16px",
            fontWeight: 500,
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(language, "manageItems.listTitle", {
            itemName: itemName.plural,
          })}
        </Typography>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              py: 2,
              paddingLeft: 2,
              // Matches the AccordionSummary's right padding + expand icon width in the body rows below.
              paddingRight: 5,
              borderBottom: "1px solid",
              borderColor: grey[300],
            }}
          >
            <Grid2 size={0.5}>
              <Checkbox
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setSelectedItemIndices(
                    isChecked
                      ? (formState.basicItems?.map((item) => item.id) ?? [])
                      : []
                  );
                }}
                icon={
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      border: "2px solid",
                      borderColor: grey[700],

                      display: "inline-block",
                      backgroundColor: "#fff",
                    }}
                  />
                }
                checkedIcon={
                  <span
                    style={{
                      width: 20,
                      height: 20,

                      border: "2px solid",
                      borderColor: grey[700],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                    }}
                  >
                    <CheckIcon
                      style={{
                        fontSize: 18,
                        color: grey[700],
                      }}
                    />
                  </span>
                }
                checked={
                  selectedItemIndices?.length === formState.basicItems?.length
                }
              />
            </Grid2>
            <Grid2
              size={itemNameColumnSize}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getLocalizedMessage(language, "manageItems.itemName", {
                itemName: itemName.single,
              })}
            </Grid2>
            {showMaturity && (
              <Grid2
                size={2}
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getLocalizedMessage(language, "maturity") || "Maturity"}
              </Grid2>
            )}
            {showDimension && (
              <Grid2
                size={2}
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getLocalizedMessage(language, "dimension") || "Dimension"}
              </Grid2>
            )}
            <Grid2
              size={2}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                minWidth: 0,
              }}
            >
              {selectedItemIndices?.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => handleDelete(selectedItemIndices)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getLocalizedMessage(language, "deleteSelected")}
                </Button>
              )}

              {showUploadExcel && (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getLocalizedMessage(language, "manageItems.uploadExcel")}
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const event = new CustomEvent("excel-file-selected", {
                          detail: { file },
                        });
                        document.dispatchEvent(event);
                      }
                    }}
                  />
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={handleAppend}
                startIcon={<Add />}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getLocalizedMessage(language, "addItem", {
                  itemName: itemName.single,
                })}
              </Button>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              overflowY: "auto",
              width: "100%",
            }}
          >
            {formState?.basicItems
              ?.filter((item) => item.flag !== "delete")
              ?.map((field, index) => (
                <BasicItemRow
                  key={field.id}
                  index={index}
                  field={field}
                  itemName={itemName}
                  projectName={projectName}
                  formState={formState}
                  onFieldChange={handleBasicItemFieldChange}
                  setValue={setValue}
                  handleDelete={handleDelete}
                  selectedItemIndices={selectedItemIndices}
                  setSelectedItemIndices={setSelectedItemIndices}
                  currentSelectedItem={currentSelectedItem}
                  setCurrentSelectedItem={setCurrentSelectedItem}
                  language={language}
                  showMaturity={showMaturity}
                  showDimension={showDimension}
                ></BasicItemRow>
              ))}
            {(formState?.basicItems?.length ?? 0) > 6 && (
              <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
            )}
          </Box>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAppend}
            startIcon={<Add />}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              padding: "7px 16px",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {getLocalizedMessage(language, "addItem", {
              itemName: itemName.single,
            })}
          </Button>
        </Box>
      </Box>
      {/* Delete Confirmation Dialog */}
      <PMPopUp
        open={openDelete}
        title={getLocalizedMessage(language, "dialog.title.confirmDelete")}
        description={
          currentSelectedItem?.length === 1
            ? getLocalizedMessage(language, "dialog.message.genericConfirm", {
                itemName: itemName.single,
              })
            : getLocalizedMessage(
                language,
                "dialog.message.genericConfirmMulti",
                {
                  itemCount: currentSelectedItem?.length ?? 0,
                  itemName: itemName.plural,
                }
              )
        }
        onClose={() => handleCloseDelete()}
        maxWidth="sm"
        cancelButtonText={getLocalizedMessage(language, "dialog.button.cancel")}
        cancelButtonFireEvent={() => handleCloseDelete()}
        cancelButtonColor="white"
        confirmationButtonColor="error"
        confirmationButtonText={getLocalizedMessage(
          language,
          "dialog.button.delete"
        )}
        confirmationButtonFireEvent={() => {
          handleConfirmDelete();
        }}
      />
    </Box>
  );
};
